
import { Component, Prop, Vue } from 'vue-property-decorator';
import { GeocodeResult } from '@googlemaps/google-maps-services-js';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

import GoogleMaps from '../../anonymous/GoogleMaps.vue';
import VendorLocations from '../vendor-location/VendorLocations.vue';
import AddDocument from '../document/AddDocument.vue';

import { sharedState } from '../../../../framework/state';
import { getResource, putResource } from '../../../../framework/client/resource';
import { getEnumerationOptions } from '../../../../framework/service/options';

import TextField from '../../../../framework/fields/TextField.vue';
import SelectField from '../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../framework/fields/SwitchField.vue';

import { parseGeocodeResult } from '../../../../../common/application/service/google_maps_service';
import { VendorType } from '../../../../../common/application/model/enums/VendorType';
import { Vendor } from '../../../../../common/application/model/Vendor';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import { successToast, errorToast } from '../../../service/toast_service';
import { InvoicePeriodizationType } from '../../../../../common/application/model/enums/InvoicePeriodizationType';
import PeriodizationTypeSelector from '../../shared/PeriodizationTypeSelector.vue';
import { InvoicePeriodizationOffsetDirection } from '../../../../../common/application/model/enums/InvoicePeriodizationOffsetDirection';
import { PeriodizationModel } from '../../../../../common/application/model/PeriodizationModel';

@Component({
    components: {
        SelectField,
        TextField,
        SwitchField,
        GoogleMaps,
        ValidationObserver,
        VendorLocations,
        AddDocument,
        ValidationProvider,
        PeriodizationTypeSelector,
    },
    beforeRouteEnter(to: any, from: any, next: any) {
        next((vm: EditVendor) => {
            vm.getPrevRouter(from); //get the previous URL
        });
    },
})
export default class EditVendor extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VENDOR;

    // Properties
    @Prop(String) readonly id!: string;

    // State
    shared = sharedState;
    local = {
        row: {} as Vendor,
        periodizationModel: {
            type: InvoicePeriodizationType.UNSPECIFIED,
            useOffset: false,
            direction: InvoicePeriodizationOffsetDirection.ADD,
            days: 0,
        } as PeriodizationModel,
        vendorTypeOptions: [] as { id: string | undefined; label: string }[],
        activeTab: 0,
        prevRoute: '',
    };

    onPeriodizationModelChanged(newModel: PeriodizationModel) {
        this.local.periodizationModel = newModel;
        this.local.row = {
            ...this.local.row,
            invoicePeriodizationType: newModel.type,
            invoicePeriodizationUseOffset: newModel.useOffset,
            invoicePeriodizationOffsetDirection: newModel.direction,
            invoicePeriodizationOffsetDays: newModel.days,
        };
    }

    validatePeriodization(value: any) {
        if (!value || !value.type || value.type.toLowerCase() === 'unspecified') {
            return this.$t('message.invoicePeriodizationTypeRequired');
        }
        if (value.useOffset) {
            if (!value.direction) {
                return this.$t('message.offsetDirectionRequired');
            }
            if (!value.days || isNaN(value.days) || Number(value.days) <= 0) {
                return this.$t('message.offsetDaysRequired');
            }
        }
        return true;
    }

    // Functions
    async mounted() {
        this.local.activeTab = Number(this.$route.params.activeTab);
        this.local.vendorTypeOptions = getEnumerationOptions('VendorType', Object.keys(VendorType), this);
        this.local.row = (await getResource<Vendor>(this.resourceType, this.id))!!;

        this.local.periodizationModel = {
            type: this.local.row.invoicePeriodizationType,
            useOffset: this.local.row.invoicePeriodizationUseOffset ?? false,
            direction: this.local.row.invoicePeriodizationOffsetDirection ?? InvoicePeriodizationOffsetDirection.ADD,
            days: this.local.row.invoicePeriodizationOffsetDays ?? 0,
        };
    }

    async save() {
        await this.saveResource(false);
    }

    async saveAndClose() {
        await this.saveResource(true);
    }

    async saveResource(goBack: boolean) {
        try {
            await putResource(this.resourceType, this.id, this.local.row);
            successToast(this, 'message.vendorSaved');

            if (goBack) {
                this.back();
            }
        } catch (error) {
            errorToast(this);
        }
    }

    back() {
        this.$router.go(-1);
    }

    get coords(): google.maps.LatLngLiteral {
        return {
            lat: this.local.row.lat,
            lng: this.local.row.lng,
        };
    }

    get fullAddress(): string {
        if (!this.local.row.vendorAddressStreet) {
            return '';
        }

        return `${this.local.row.vendorAddressStreet}, ${this.local.row.vendorAddressZip} ${this.local.row.vendorAddressCity}`;
    }

    setAddress(geocodeResult: GeocodeResult) {
        const parsedLocation = parseGeocodeResult(geocodeResult);

        this.local.row = {
            ...this.local.row,
            vendorAddressStreet: parsedLocation.street,
            vendorAddressZip: parsedLocation.zip,
            vendorAddressCity: parsedLocation.city,
            vendorAddressCountry: parsedLocation.country,
            lat: parsedLocation.lat,
            lng: parsedLocation.lng,
        };
    }

    getPrevRouter(from: any) {
        this.local.prevRoute = from.path;
        if (this.local.prevRoute.includes('/file-meta/')) {
            this.local.activeTab = 1;
        }
    }
}
